import React, { Component } from 'react'
import { withApollo } from 'react-apollo'
import gql from 'graphql-tag'
import { store } from '../../stores/reduxStore'
import { Card, CardHeader, CardBody, Table, Input, FormGroup, Label, Col, Row } from 'reactstrap'
import { RiseLoader } from 'react-spinners'

const PROJECTS_QUERY = gql`
  query SectionProgressProjects {
    projects {
      list { id name project_companies }
    }
  }
`

const PERSON_QUERY = gql`
  query SectionProgressPerson($person_id: ID!) {
    person(id: $person_id) { company_ids }
  }
`

const DATA_QUERY = gql`
  query StatsSectionProgress($project_id: ID!) {
    stats_section_progress(project_id: $project_id) {
      project_id project_name planned_start_date planned_end_date
      sections {
        section_id section_name section_depth
        welds_total welds_ok welds_ready welds_in_progress welds_other welds_complete
        welding_mm welding_done_mm welding_logs_total welding_logs_done_mm
        ri_required_mm ri_done_mm ri_logs_count ri_complete ri_total
        ndt_required_mm ndt_done_mm ndt_logs_count ndt_complete ndt_total
        by_type { type_id type_name required_mm done_mm logs_count complete total }
      }
    }
  }
`


const pctColor = (pct) => pct >= 80 ? '#28a745' : pct >= 40 ? '#ffc107' : '#dc3545'
const pct = (done, total) => total > 0 ? Math.round(done * 100 / total) : 0

const PctBar = ({ done, total }) => {
  const p = pct(done, total)
  return (
    <div style={{ position: 'relative', minWidth: '100px' }}>
      <div className="progress" style={{ height: '16px', border: '1px solid #aaa', backgroundColor: '#e0e0e0' }}>
        <div className="progress-bar" style={{ width: `${Math.min(p, 100)}%`, backgroundColor: pctColor(p) }} />
      </div>
      <span style={{ position: 'absolute', top: 0, left: 0, right: 0, textAlign: 'center', fontSize: '11px', fontWeight: 'bold', color: '#666', lineHeight: '16px' }}>{p}%</span>
    </div>
  )
}

class SectionProgress extends Component {
  constructor(props) {
    super(props)
    this.state = {
      projects: [], userCompanyIds: [], selectedProjectId: null,
      data: null, loading: false, projectsLoading: true, error: null,
      sortBy: 'section_name', sortDir: 'asc', sectionFilter: '', selectedRows: new Set()
    }
  }

  componentDidMount() {
    const personId = store.getState().user.data.person_id
    const params = new URLSearchParams(this.props.location && this.props.location.search)
    const urlProjectId = params.get('project_id')
    Promise.all([
      this.props.client.query({ query: PROJECTS_QUERY, fetchPolicy: 'network-only' }),
      this.props.client.query({ query: PERSON_QUERY, variables: { person_id: personId }, fetchPolicy: 'network-only' })
    ]).then(([projectsRes, personRes]) => {
      this.setState({
        projects: projectsRes.data.projects.list,
        userCompanyIds: (personRes.data.person.company_ids || []).map(String),
        projectsLoading: false
      }, () => { if (urlProjectId) this.handleProjectChange(urlProjectId) })
    }).catch(e => this.setState({ error: e.message, projectsLoading: false }))
  }

  handleProjectChange(projectId) {
    if (!projectId) {
      this.setState({ selectedProjectId: null, data: null })
      this.props.history && this.props.history.replace('/reports/section-progress')
      return
    }
    this.setState({ selectedProjectId: projectId, data: null, loading: true, error: null })
    this.props.history && this.props.history.replace(`/reports/section-progress?project_id=${projectId}`)
    this.props.client.query({ query: DATA_QUERY, fetchPolicy: 'network-only', variables: { project_id: projectId } })
      .then(({ data }) => this.setState({ data: data.stats_section_progress[0] || null, loading: false }))
      .catch(e => this.setState({ error: e.message, loading: false }))
  }

  toggleSort(col) {
    this.setState(s => ({ sortBy: col, sortDir: s.sortBy === col && s.sortDir === 'asc' ? 'desc' : 'asc' }))
  }

  sorted(sections) {
    const { sortBy, sortDir, sectionFilter } = this.state
    const filtered = sectionFilter
      ? sections.filter(s => s.section_name.toLowerCase().includes(sectionFilter.toLowerCase()))
      : sections
    if (sortBy === 'section_name' && sortDir === 'asc') return filtered // preserve tree order
    return [...filtered].sort((a, b) => {
      const av = a[sortBy], bv = b[sortBy]
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
      return sortDir === 'asc' ? (av || 0) - (bv || 0) : (bv || 0) - (av || 0)
    })
  }

  render() {
    const { projects, userCompanyIds, selectedProjectId, data, loading, projectsLoading, error } = this.state

    const filteredProjects = projects.filter(p => {
      if (!userCompanyIds.length) return true
      try {
        const pc = typeof p.project_companies === 'string' ? JSON.parse(p.project_companies) : (p.project_companies || [])
        return pc.some(c => userCompanyIds.includes(String(c.id)))
      } catch (e) { return false }
    })

    return (
      <div className="animated fadeIn">
        <Card>
          <CardHeader><strong><i className="fa fa-sitemap mr-2" />Section Progress</strong></CardHeader>
          <CardBody style={{ position: 'relative', minHeight: '200px' }}>
            {(loading || projectsLoading) && <div className="spinner-item" id="loader-bg"><RiseLoader color="#20a8d8" loading={true} /></div>}
            {error && <div className="text-danger">Error: {error}</div>}

            <Row className="mb-3">
              <Col sm={4}>
                <FormGroup>
                  <Label>Project</Label>
                  <Input type="select" value={selectedProjectId || ''}
                    onChange={e => this.handleProjectChange(e.target.value || null)}>
                    <option value="">— select project —</option>
                    {filteredProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            {data && (data.planned_start_date || data.planned_end_date) && (
              <Row className="mb-3">
                {data.planned_start_date && (
                  <Col sm="auto">
                    <div className="p-3 border rounded text-center">
                      <div className="text-muted small">Planned start</div>
                      <div className="h5 mb-0"><strong>{data.planned_start_date}</strong></div>
                    </div>
                  </Col>
                )}
                {data.planned_end_date && (
                  <Col sm="auto">
                    <div className="p-3 border rounded text-center">
                      <div className="text-muted small">Planned end</div>
                      <div className="h5 mb-0"><strong>{data.planned_end_date}</strong></div>
                    </div>
                  </Col>
                )}
              </Row>
            )}

            {data && (
              <Row className="mb-3">
                <Col sm={4}>
                  <FormGroup>
                    <Label>Filter section</Label>
                    <div className="d-flex" style={{ gap: '8px' }}>
                      <Input type="text" placeholder="Search..." value={this.state.sectionFilter}
                        onChange={e => this.setState({ sectionFilter: e.target.value })} />
                      {this.state.selectedRows.size > 0 && (
                        <button className="btn btn-sm btn-outline-secondary text-nowrap"
                          onClick={() => this.setState({ selectedRows: new Set() })}>
                          Clear selection ({this.state.selectedRows.size})
                        </button>
                      )}
                    </div>
                  </FormGroup>
                </Col>
              </Row>
            )}

            {!selectedProjectId && !loading && (
              <div className="text-center text-muted py-5">Select a project to view section progress</div>
            )}

            {data && !loading && (() => {
              const sections = this.sorted(data.sections)
              const th = (col, label) => (
                <th key={col} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => this.toggleSort(col)}>
                  {label} {this.state.sortBy === col ? (this.state.sortDir === 'desc' ? '▼' : '▲') : '⇅'}
                </th>
              )

              const ndtTypesSeen = new Map()
              sections.forEach(s => (s.by_type || []).forEach(t => {
                if (!ndtTypesSeen.has(t.type_id)) ndtTypesSeen.set(t.type_id, t.type_name)
              }))
              const ndtTypes = Array.from(ndtTypesSeen, ([type_id, type_name]) => ({ type_id, type_name }))

              return (
                <div>
                  <div style={{ overflowX: 'auto' }}>
                    <Table bordered striped size="sm" style={{ minWidth: '1400px' }}>
                      <thead>
                        <tr>
                          {th('section_name', 'Section')}
                          <th colSpan={6} className="text-center" style={{ backgroundColor: '#f0f4ff' }}>Welds</th>
                          <th colSpan={4} className="text-center" style={{ backgroundColor: '#f0fff0' }}>Welding</th>
                          <th colSpan={4} className="text-center" style={{ backgroundColor: '#fff0f0' }}>RI</th>
                          <th colSpan={4} className="text-center" style={{ backgroundColor: '#fffbe6' }}>NDT (all)</th>
                          {ndtTypes.map(({ type_id: tid, type_name: tname }) => (
                            <th key={tid} colSpan={4} className="text-center" style={{ backgroundColor: '#f5f5f5' }}>{tname}</th>
                          ))}
                        </tr>
                        <tr>
                          <th></th>
                          <th className="text-center" style={{ backgroundColor: '#f0f4ff' }}>Total</th>
                          <th className="text-center" style={{ backgroundColor: '#f0f4ff', color: '#28a745' }}>OK</th>
                          <th className="text-center" style={{ backgroundColor: '#f0f4ff', color: '#17a2b8' }}>Ready</th>
                          <th className="text-center" style={{ backgroundColor: '#f0f4ff', color: '#007bff' }}>In progress</th>
                          <th className="text-center" style={{ backgroundColor: '#f0f4ff', color: '#6c757d' }}>Other</th>
                          <th className="text-center" style={{ backgroundColor: '#f0f4ff' }}>Complete</th>
                          <th className="text-center" style={{ backgroundColor: '#f0fff0' }}>Total (mm)</th>
                          <th className="text-center" style={{ backgroundColor: '#f0fff0' }}>Done (mm)</th>
                          <th className="text-center" style={{ backgroundColor: '#f0fff0' }}>Logs welding</th>
                          <th className="text-center" style={{ backgroundColor: '#f0fff0' }}>Complete</th>
                          <th className="text-center" style={{ backgroundColor: '#fff0f0' }}>Required (mm)</th>
                          <th className="text-center" style={{ backgroundColor: '#fff0f0' }}>Done (mm)</th>
                          <th className="text-center" style={{ backgroundColor: '#fff0f0' }}>Count logs</th>
                          <th className="text-center" style={{ backgroundColor: '#fff0f0' }}>Complete</th>
                          <th className="text-center" style={{ backgroundColor: '#fffbe6' }}>Required (mm)</th>
                          <th className="text-center" style={{ backgroundColor: '#fffbe6' }}>Done (mm)</th>
                          <th className="text-center" style={{ backgroundColor: '#fffbe6' }}>Count logs</th>
                          <th className="text-center" style={{ backgroundColor: '#fffbe6' }}>Complete</th>
                          {ndtTypes.map(({ type_id: tid }) => [
                            <th key={`${tid}_r`} className="text-center" style={{ backgroundColor: '#f5f5f5' }}>Required (mm)</th>,
                            <th key={`${tid}_d`} className="text-center" style={{ backgroundColor: '#f5f5f5' }}>Done (mm)</th>,
                            <th key={`${tid}_l`} className="text-center" style={{ backgroundColor: '#f5f5f5' }}>Count logs</th>,
                            <th key={`${tid}_c`} className="text-center" style={{ backgroundColor: '#f5f5f5' }}>Complete</th>
                          ])}
                        </tr>
                      </thead>
                      <tbody>
                        {sections.map(s => {
                          const byType = (s.by_type || []).reduce((acc, t) => { acc[t.type_id] = t; return acc }, {})
                          return (
                            <tr key={s.section_id}
                              onClick={() => this.setState(st => {
                                const rows = new Set(st.selectedRows)
                                rows.has(s.section_id) ? rows.delete(s.section_id) : rows.add(s.section_id)
                                return { selectedRows: rows }
                              })}
                              style={{ cursor: 'pointer', backgroundColor: this.state.selectedRows.has(s.section_id) ? '#cfe8ff' : '' }}>
                              <td style={{ minWidth: '250px', paddingLeft: `${((s.section_depth || 2) - 2) * 16 + 8}px` }}><strong>{s.section_name}</strong></td>
                              <td className="text-center">{s.welds_total}</td>
                              <td className="text-center" style={{ color: '#28a745' }}>{s.welds_ok}</td>
                              <td className="text-center" style={{ color: '#17a2b8' }}>{s.welds_ready}</td>
                              <td className="text-center" style={{ color: '#007bff' }}>{s.welds_in_progress}</td>
                              <td className="text-center" style={{ color: '#6c757d' }}>{s.welds_other}</td>
                              <td className="text-center"><PctBar done={s.welds_ok} total={s.welds_total} /></td>
                              <td className="text-center">{(s.welding_mm || 0).toLocaleString()}</td>
                              <td className="text-center">{(s.welding_logs_done_mm || 0).toLocaleString()}</td>
                              <td className="text-center">{(s.welding_logs_total || 0).toLocaleString()}</td>
                              <td className="text-center"><PctBar done={s.welds_complete || 0} total={s.welds_total} /></td>
                              <td className="text-center">{(s.ri_required_mm || 0).toLocaleString()}</td>
                              <td className="text-center">{(s.ri_done_mm || 0).toLocaleString()}</td>
                              <td className="text-center">{(s.ri_logs_count || 0).toLocaleString()}</td>
                              <td className="text-center"><PctBar done={s.ri_complete || 0} total={s.ri_total || 0} /></td>
                              <td className="text-center">{(s.ndt_required_mm || 0).toLocaleString()}</td>
                              <td className="text-center">{(s.ndt_done_mm || 0).toLocaleString()}</td>
                              <td className="text-center">{(s.ndt_logs_count || 0).toLocaleString()}</td>
                              <td className="text-center"><PctBar done={s.ndt_complete || 0} total={s.ndt_total || 0} /></td>
                              {ndtTypes.map(({ type_id: tid }) => {
                                const t = byType[tid] || { required_mm: 0, done_mm: 0, logs_count: 0, complete: 0 }
                                return [
                                  <td key={`${tid}_r`} className="text-center">{(t.required_mm || 0).toLocaleString()}</td>,
                                  <td key={`${tid}_d`} className="text-center">{(t.done_mm || 0).toLocaleString()}</td>,
                                  <td key={`${tid}_l`} className="text-center">{(t.logs_count || 0).toLocaleString()}</td>,
                                  <td key={`${tid}_c`} className="text-center"><PctBar done={t.complete || 0} total={t.total || 0} /></td>
                                ]
                              })}
                            </tr>
                          )
                        })}
                      </tbody>
                    </Table>
                  </div>

                  <div className="mt-3 p-3 border rounded bg-light" style={{ fontSize: '12px' }}>
                    <strong>How it's calculated:</strong>
                    <ul className="mb-0 mt-1 pl-3">
                      <li><strong>Welds Total/OK/Ready/In progress/Other</strong> — weld status counts (removed excluded); Other = attachments_incomplete, inconsistent, new</li>
                      <li><strong>Welds Complete</strong> — welds_ok / welds_total</li>
                      <li><strong>Welding Total (mm)</strong> — sum of weld lengths</li>
                      <li><strong>Welding Done (mm)</strong> — sum of mm from welding logs</li>
                      <li><strong>Welding Count logs</strong> — number of welding logs</li>
                      <li><strong>Welding Complete</strong> — welds with welding_progress = 100% / welds_total</li>
                      <li><strong>RI/NDT Required (mm)</strong> — sum of weld lengths × range% from inspection_categories (×2 if double_length)</li>
                      <li><strong>RI/NDT Done (mm)</strong> — sum of mm from inspection logs (×2 if double_length on log)</li>
                      <li><strong>RI/NDT Count logs</strong> — number of inspection logs for that type</li>
                      <li><strong>RI Complete</strong> — welds with RI complete_percent = 100 (non-representative group members count as complete) / welds requiring RI</li>
                      <li><strong>NDT (all) Complete</strong> — welds where ALL required NDT types are complete (considering groups) / welds with any NDT in category</li>
                      <li><strong>VT/MT/RT/UT/UT-PA Complete</strong> — welds with that type complete (considering groups) / welds requiring that type</li>
                    </ul>
                  </div>
                </div>
              )
            })()}
          </CardBody>
        </Card>
      </div>
    )
  }
}

export default withApollo(SectionProgress)



// WEBPACK FOOTER //
// ./src/components/Report/SectionProgress.js